import { useCallback, useEffect, useRef } from "react";

const DEFAULT_ASSISTANT_AUDIO_CONTENT_TYPE = "audio/mpeg";

// Isolates the imperative browser Audio lifecycle so replacing or unmounting the
// interview always stops playback and every action shares the same fallback behavior.
const useOralInterviewAudioPlayback = ({
  onAssistantAudioUnavailable,
  onAssistantSpeaking,
  onReadyToSpeak
}) => {
  const assistantAudioRef = useRef(null);

  const stopAssistantAudioPlayback = useCallback(() => {
    const assistantAudio = assistantAudioRef.current;
    assistantAudioRef.current = null;

    if (!assistantAudio) return;

    assistantAudio.pause();
    assistantAudio.src = "";
  }, []);

  const playAudio = useCallback(
    ({
      audioResult,
      onAudioMissing,
      onPlaybackEnded,
      onPlaybackRejected,
      onPlaybackStarted
    }) => {
      const audioBase64 = audioResult?.assistantAudioBase64;
      const audioContentType =
        audioResult?.assistantAudioContentType || DEFAULT_ASSISTANT_AUDIO_CONTENT_TYPE;
      if (!audioBase64) {
        onAudioMissing?.();
        return;
      }

      stopAssistantAudioPlayback();
      onPlaybackStarted?.();

      const assistantAudio = new Audio(`data:${audioContentType};base64,${audioBase64}`);
      const finishPlayback = callback => {
        if (assistantAudioRef.current !== assistantAudio) return;

        assistantAudioRef.current = null;
        callback?.();
      };

      assistantAudioRef.current = assistantAudio;
      assistantAudio.onended = () => finishPlayback(onPlaybackEnded);
      assistantAudio.onerror = () => finishPlayback(onPlaybackEnded);
      assistantAudio.play().catch(() => finishPlayback(onPlaybackRejected));
    },
    [stopAssistantAudioPlayback]
  );

  const playAssistantAudio = useCallback(
    audioResult => {
      playAudio({
        audioResult,
        onAudioMissing: onAssistantAudioUnavailable,
        onPlaybackEnded: onReadyToSpeak,
        onPlaybackRejected: onAssistantAudioUnavailable,
        onPlaybackStarted: onAssistantSpeaking
      });
    },
    [onAssistantAudioUnavailable, onAssistantSpeaking, onReadyToSpeak, playAudio]
  );

  const playCompletionAudio = useCallback(
    audioResult => playAudio({ audioResult }),
    [playAudio]
  );

  useEffect(() => {
    return () => {
      stopAssistantAudioPlayback();
    };
  }, [stopAssistantAudioPlayback]);

  return {
    playAssistantAudio,
    playCompletionAudio,
    stopAssistantAudioPlayback
  };
};

export default useOralInterviewAudioPlayback;
